import React, { FC, useEffect, useState, useMemo, useCallback, ReactElement } from 'react';
import { BlockType, BlockWithNotes } from '../../../../config/api/models/Block';
import Block from './Block';
import {
  TrajectorySectionOrWorksheetType,
  trajectorySectionNameLookup,
} from '../../../../config/api/models/Trajectory';
import { UserBlockAnswers } from '../../../../config/api/models/Answers';
import { GroupStudentSeriesScores } from '../../../../config/api/models/Score';
import { AddBlockButtonGroup } from './AddBlockButton';
import { BlockDropArea, PageAggregatedBlocks, PageWrapper } from './Atoms';
import { FeedbackModule } from 'src/modules/feedback';
import { useDisclosure } from 'react-use-disclosure';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { setSelectedBlock } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import { getBlockNotes } from 'src/modules/shared/slices/notes/api/notes';
import { setBlockNotes } from 'src/modules/shared/slices/notes/entity/BlockNotesSlice';
import { useUser } from 'src/modules/shared';

export type UpdatedBlockModel = BlockWithNotes & {
  onSelectBlock?: Function | undefined;
};

type BlocksListProps = {
  pageId: string;
  pageType: TrajectorySectionOrWorksheetType;
  readOnly?: boolean;
  blocks: UpdatedBlockModel[];
  onUpdate?: () => void;
  teacherProgressAndScores?: GroupStudentSeriesScores;
  userAnswersFromPage?: UserBlockAnswers;
  groupAnswersFromPage?: UserBlockAnswers[];
  clusterId?: string;
  sectionId?: string;
};

export const PageConditionalWrapper = ({
  condition,
  children,
}: {
  condition: boolean;
  children: ReactElement;
}) => {
  if (condition)
    return (
      <PageWrapper style={{ padding: '2rem' }} elevation={0}>
        {children}
      </PageWrapper>
    );
  return children;
};

export const BlocksList: FC<BlocksListProps> = ({
  pageId,
  pageType,
  readOnly,
  blocks,
  onUpdate,
  teacherProgressAndScores,
  clusterId = undefined,
  sectionId = undefined,
}) => {
  const { isAuthor } = useUser();
  const bookId = useAppSelector((state) => state.trajectories.currentBookId);
  const [feedbackBlockId, setFeedbackBlockId] = useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const selectedBlock = useAppSelector((state) => state.blocks.selectedBlock);
  const blockNotes = useAppSelector((state) => state.blockNotes.blockNotes);
  const targetBlock = useAppSelector((state) => state.blocks.targetBlock);

  const hanldleSetFeedbackBlockId = (id: string) => {
    setFeedbackBlockId(id);
  };

  const handleSelectBlock = useCallback(
    (blockId: string | undefined) => {
      dispatch(setSelectedBlock(blockId));
    },
    [dispatch]
  );

  const {
    isOpen: feedbackIsOpen,
    close: closeFeedback,
    toggle: toggleFeedback,
    open: openFeedback,
  } = useDisclosure(false);

  const { data: blockNotesData, isSuccess } = getBlockNotes(
    { pageId, bookId },
    { skip: isAuthor || !pageId }
  );

  useEffect(() => {
    if (isSuccess) {
      dispatch(setBlockNotes(blockNotesData));
    }
  }, [blockNotesData, isSuccess, dispatch]);

  // TODO - control concurrency between hover, feedback and target blocks
  useEffect(() => {
    if (blocks?.find((block) => block.id === feedbackBlockId)?.id) {
      openFeedback();

      setTimeout(() => {
        const indicator = document.querySelector(
          `#feedback_${blocks?.find((block) => block.id === feedbackBlockId)?.id}`
        );
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [feedbackBlockId, blocks, openFeedback]);

  useEffect(() => {
    let indicator: any = undefined;
    if (!!targetBlock) {
      if (!clusterId && !sectionId && targetBlock === pageId) {
        indicator = document.querySelector(`#droparea_${pageId}`);
      } else if (sectionId && targetBlock === sectionId + '-header') {
        indicator = document.querySelector(`#droparea_${sectionId}-header`);
      } else {
        indicator = document.querySelector(
          `#droparea_${blocks?.find((block) => block.id === targetBlock)?.id}`
        );
      }
      setTimeout(() => {
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [targetBlock, clusterId, sectionId, blocks, pageId]);

  const blocksWithNotes = useMemo(() => {
    return blocks?.map((block) => {
      const parent = block.parent_id ? block.parent_id : block.id;
      const note = blockNotes?.find((note) => note.blockId === parent);

      return { ...block, note, pageType };
    });
  }, [blocks, blockNotes, pageType]);

  const aggregatedBlocks = useMemo(() => {
    const result: { isCluster: boolean; blocks: UpdatedBlockModel[] }[] = [];
    let pageBlocks: UpdatedBlockModel[] = [];

    blocksWithNotes?.forEach((block, index) => {
      if (block.type === 'cluster') {
        if (pageBlocks.length) {
          result.push({ isCluster: false, blocks: pageBlocks });
          pageBlocks = [];
        }

        result.push({ isCluster: true, blocks: [block] });
      } else {
        pageBlocks.push(block);
        if (index === blocksWithNotes.length - 1) {
          result.push({ isCluster: false, blocks: pageBlocks });
          pageBlocks = [];
        }
      }
    });

    return result;
  }, [blocksWithNotes]);

  const showAddBlockButton = useCallback(
    (blockType: BlockType) => {
      return (
        [
          BlockType.WYSIWYG,
          BlockType.VIDEO,
          BlockType.IFRAME,
          BlockType.H5P,
          BlockType.COLUMN,
          BlockType.BOOK_WIDGET,
          BlockType.ALGEBRA_KIT,
          BlockType.STUDY_GUIDE,
          BlockType.CLUSTER,
          BlockType.TEXT_EXERCISE,
        ].includes(blockType) &&
        !readOnly &&
        Object.keys(trajectorySectionNameLookup).includes(pageType) &&
        pageType !== 'exercises'
      );
    },
    [readOnly, pageType]
  );

  const getPPreviousBlockId = useCallback(
    (aggregatedBlockIndex: number) => {
      return aggregatedBlocks[aggregatedBlockIndex - 1].blocks[0].id;
    },
    [aggregatedBlocks]
  );

  if (blocks?.length === 0) return null;

  return (
    <>
      <>
        {/* At page level if user is dragging to the page top*/}
        {!clusterId && !sectionId && targetBlock === pageId && (
          <BlockDropArea id={`droparea_${pageId}`} variant="rect" height="40px" />
        )}
        {/* At cluster level if user is dragging to the cluster top*/}
        {sectionId && targetBlock === sectionId + '-header' && (
          <BlockDropArea id={`droparea_${sectionId}-header`} variant="rect" height="40px" />
        )}
        {aggregatedBlocks?.map((aggregatedBlockItem, aggregatedBlockIndex) => {
          return (
            <PageAggregatedBlocks
              key={aggregatedBlockIndex}
              className={`aggregated-blocks-${aggregatedBlockItem.isCluster}`}
              isCluster={aggregatedBlockItem.isCluster || !!clusterId || !!sectionId}
            >
              {aggregatedBlockItem.blocks.map((block, index) => {
                return (
                  <div key={block.id}>
                    <>
                      {showAddBlockButton(block.type) &&
                        index === 0 &&
                        !clusterId &&
                        !sectionId && (
                          <AddBlockButtonGroup
                            pageId={pageId}
                            previousBlockId={
                              aggregatedBlockIndex === 0
                                ? ''
                                : getPPreviousBlockId(aggregatedBlockIndex)
                            }
                          />
                        )}
                      {feedbackBlockId === block.id && (
                        <FeedbackModule
                          pageId={pageId}
                          blockId={feedbackBlockId}
                          context="block"
                          mode="floating"
                          open={feedbackIsOpen}
                          onClose={closeFeedback}
                        />
                      )}

                      <Block
                        onSelectBlock={handleSelectBlock}
                        selectedBlock={selectedBlock}
                        bookId={bookId}
                        readOnly={readOnly}
                        pageId={pageId}
                        pageType={pageType}
                        block={block}
                        onUpdate={onUpdate}
                        teacherProgressAndScores={teacherProgressAndScores}
                        clusterId={clusterId}
                        sectionId={sectionId}
                        onSetFeedbackBlockId={hanldleSetFeedbackBlockId}
                        onToggleFeedback={toggleFeedback}
                      />
                      {targetBlock === block.id && (
                        <BlockDropArea id={`droparea_${block.id}`} variant="rect" height="40px" />
                      )}
                    </>
                    {!(
                      block.type === 'cluster' &&
                      aggregatedBlocks[aggregatedBlockIndex + 1] &&
                      aggregatedBlocks[aggregatedBlockIndex + 1].isCluster
                    ) &&
                      showAddBlockButton(block.type) && (
                        <AddBlockButtonGroup
                          previousBlockId={block.id}
                          clusterId={clusterId}
                          sectionId={sectionId}
                          pageId={pageId}
                        />
                      )}
                  </div>
                );
              })}
            </PageAggregatedBlocks>
          );
        })}
      </>
    </>
  );
};
